// ============================================================
// CustomerMenu.jsx — RESTAURANT BILLING (UPDATED)
//
// Key changes vs old code:
//  ✅ 3-step billing wizard (mirrors SPA billing modal)
//  ✅ Step 1: Discount (proportional breakdown shown per dish)
//  ✅ Step 2: GST Rate per dish (editable, 0 = non-GST)
//  ✅ Step 3: Payment status + summary
//  ✅ All amounts calculated server-side via applyRestaurantBilling()
//  ✅ Invoice HTML uses line_items for per-dish GST/Non-GST split
// ============================================================

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { lookupGuestByMobile } from "../../helpers/guestLookup";
import { Table, Tabs, Tab, Button, Modal, Form, Pagination } from "react-bootstrap";
import { FaPrint, FaPlus, FaEdit, FaFileExcel } from "react-icons/fa";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import BillingStatusBadge from "../../customcomponents/BillingStatusBadge";
import {
  getAllguestfiedsAPI,
  addcustomerAPI,
  getAllCustomerAPI,
  deleteCustomerAPI,
  getCustomerbillAPI,
  gethotelinfoIdAPI,
  applyRestaurantBillingAPI,   // ✅ NEW endpoint
  BASE_URL_for_images,
} from "../../helpers/api.ts";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const pageSize = 10;
const clampMoney = (n) => { const x = Number(n); return !isFinite(x) || x < 0 ? 0 : x; };

// ─── Core proportional billing calculator (mirrors calculateRestaurantBilling) ─
function calcBilling(dishLines, totalDiscount) {
  const subtotal = dishLines.reduce((s, l) => s + l.rate * l.qty, 0);
  const discount = Math.min(clampMoney(totalDiscount), subtotal);

  const lines = dishLines.map((l) => {
    const lineAmt      = parseFloat((l.rate * l.qty).toFixed(2));
    const lineDiscount = subtotal > 0
      ? parseFloat(((lineAmt / subtotal) * discount).toFixed(2)) : 0;
    const netAmt  = parseFloat((lineAmt - lineDiscount).toFixed(2));
    const lineGst = parseFloat((netAmt * l.gst_rate).toFixed(2));
    const lineTotal = parseFloat((netAmt + lineGst).toFixed(2));
    return { ...l, lineAmt, lineDiscount, netAmt, lineGst, lineTotal };
  });

  const totalGst     = lines.reduce((s, l) => s + l.lineGst, 0);
  const grandTotal   = lines.reduce((s, l) => s + l.lineTotal, 0);
  const netTotal     = lines.reduce((s, l) => s + l.netAmt, 0);
  const effGstPct    = netTotal > 0 ? parseFloat(((totalGst / netTotal) * 100).toFixed(2)) : 0;

  return { subtotal, discount, totalGst, grandTotal, effGstPct, lines };
}


const PaymentStatusBadge = ({ status }) => {
  if (!status) return <span style={{ color: "#bbb", fontSize: "11px" }}>—</span>;
  const cfg = {
    PAID          : { bg: "#e8f5e9", color: "#1b5e20", border: "#43a047", label: "Paid"          },
    UNPAID        : { bg: "#fff3e0", color: "#bf360c", border: "#fb8c00", label: "Unpaid"        },
    PENDING       : { bg: "#fff8e1", color: "#f57f17", border: "#fdd835", label: "Pending"       },
    // PARTIALLY_PAID: { bg: "#e3f2fd", color: "#1565c0", border: "#90caf9", label: "Partial"       },
    CANCELLED     : { bg: "#f5f5f5", color: "#757575", border: "#bdbdbd", label: "Cancelled"     },
  };
  const s = cfg[status] || cfg.PENDING;
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.3px",
      background: s.bg, color: s.color, border: `1.5px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
};

const CustomerMenu = () => {
  const [activeTab, setActiveTab]       = useState("bills");
  const [guests, setGuests]             = useState([]);
  const [customers, setCustomers]       = useState([]);
  const [currentPage, setCurrentPage]   = useState(1);
  const [totalPages, setTotalPages]     = useState(1);

  const navigation = useNavigate();

  const [showModal, setShowModal]           = useState(false);
  const [formData, setFormData]             = useState({ mobile_number: "", name: "" });
  const [isNameEditable, setIsNameEditable] = useState(false);

  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);

  // ── Billing wizard state ────────────────────────────────────────────────
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingStep, setBillingStep]           = useState(1);      // 1 | 2 | 3
  const [billingCustomerId, setBillingCustomerId] = useState(null);
  const [billingAction, setBillingAction]       = useState("DOWNLOAD");
  const [billData, setBillData]                 = useState(null);   // from getCustomerbillAPI

  // Per-dish GST overrides: { [dish_id]: gst_percent_number }
  const [editableGstRates, setEditableGstRates] = useState({});
  const [discountInput, setDiscountInput]       = useState(0);
  const [billingStatus, setBillingStatus]       = useState("PENDING");

  // Computed result (FE preview — server recalculates on submit)
  const [calcResult, setCalcResult]             = useState(null);

  const [exportLoading, setExportLoading]       = useState(false);
  const [showExportModal, setShowExportModal]   = useState(false);
  const [exportDateRange, setExportDateRange]   = useState({ startDate: "", endDate: "" });

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const hotel_id1 = useMemo(() => {
    if (["RECEPTIONIST", "SPA", "FOOD AND BEVERAGE", "MANAGER"].includes(user?.role)) {
      return user?.hotel?.hotel_id;
    }
    return user?.hotel_id;
  }, [user]);

  useEffect(() => { fetchGuests(); fetchCustomers(); }, [currentPage]);

  const fetchGuests = async () => {
    try {
      const response = await getAllguestfiedsAPI({ hotel_id: hotel_id1, pagination: { page: currentPage, pageSize } });
      if (response?.success) setGuests(response?.data?.guests || []);
    } catch (e) { console.error(e); }
  };

  // In fetchCustomers() in CustomerMenu.jsx:
const fetchCustomers = async () => {
  try {
    const response = await getAllCustomerAPI({ hotel_id: hotel_id1, currentPage, pageSize });
    if (response?.success) {
      // ✅ Try both shapes — old and new
      const customersList = response?.data?.customers || [];
      const paginationInfo = response?.data?.pagination || {};
      
      setCustomers(customersList);
      setTotalPages(paginationInfo?.totalPages ?? response?.data?.pages ?? 1);
    }
  } catch (e) { console.error(e); }
};

  // ── Build dishLines from billData.menuItems for the calculator ──────────
  const buildDishLines = useCallback((menuItems, gstRates) => {
    return (menuItems || []).map((item) => ({
      dish_id : item.menuId,
      dish_name: item.dish,
      qty     : Number(item.quantity) || 1,
      rate    : Number(item.cost) || 0,
      gst_rate: (gstRates[item.menuId] ?? Number(item.gst_percentage ?? 0)) / 100,
    }));
  }, []);

  // Recompute whenever discount or gstRates change
  useEffect(() => {
    if (!billData?.menuItems) return;
    const lines  = buildDishLines(billData.menuItems, editableGstRates);
    const result = calcBilling(lines, discountInput);
    setCalcResult(result);
  }, [discountInput, editableGstRates, billData, buildDishLines]);

  // ── Open billing wizard ──────────────────────────────────────────────────
  const handleBillingModalShow = async (customerId, action = "DOWNLOAD") => {
    try {
      const response = await getCustomerbillAPI(customerId);
      if (!response?.success || !response?.menuItems?.length) {
        toast.warning("This customer has no menu items to bill.");
        return;
      }

      // Initialise GST rates from existing billingData if available
      const initialGst = {};
      (response.menuItems || []).forEach((item) => {
        initialGst[item.menuId] = Number(item.gst_percentage ?? item.billingData?.gst_percentage ?? 0);
      });

      // Existing discount
      const existingDiscount = (response.menuItems || []).reduce(
        (s, item) => s + clampMoney(item.billingData?.discount || 0), 0
      );

      // Derive existing billing status
      const status = deriveStatusFromSummary(response.billingStatusSummary);

      setBillingCustomerId(customerId);
      setBillingAction(action);
      setBillData(response);
      setEditableGstRates(initialGst);
      setDiscountInput(existingDiscount);
      setBillingStatus(status);
      setBillingStep(1);
      setShowBillingModal(true);
    } catch (e) {
      console.error(e);
      toast.error("Error fetching bill data");
    }
  };

  const handleBillingModalClose = () => {
    setShowBillingModal(false);
    setBillData(null);
    setCalcResult(null);
    setEditableGstRates({});
    setDiscountInput(0);
    setBillingStep(1);
  };

  const deriveStatusFromSummary = (summary) => {
    if (!summary) return "PENDING";
    const { PENDING = 0, PAID = 0, PARTIALLY_PAID = 0, CANCELLED = 0 } = summary;
    const totalActive = PENDING + PAID + PARTIALLY_PAID;
    if (totalActive === 0) return CANCELLED > 0 ? "CANCELLED" : "PENDING";
    if (PARTIALLY_PAID > 0 || (PAID > 0 && PENDING > 0)) return "PARTIALLY_PAID";
    if (PAID > 0) return "PAID";
    return "PENDING";
  };

  // ── Apply billing (Step 3 → Submit) ─────────────────────────────────────
  const handleApplyBilling = async () => {
  try {
    const gstOverridesPayload = { ...editableGstRates };

    const existingReservationId =
      billData?.menuItems?.find((item) => item.billingData?.reservation_id)
        ?.billingData?.reservation_id || null;

    // ✅ Rule:
    // PAID = customer paid directly at restaurant
    // PENDING / PARTIALLY_PAID = charge to room
    const normalizedStatus = String(billingStatus || "PENDING").toUpperCase();

    const isDirectPayment = normalizedStatus === "PAID";

    const billingType = isDirectPayment ? "DIRECT" : "ROOM";

    const response = await applyRestaurantBillingAPI(billingCustomerId, {
      discount: discountInput,
      gst_overrides: gstOverridesPayload,
      billing_status: normalizedStatus,

      // ✅ Keep reservation_id if you want final hotel bill to show status for this stay.
      // Amount inclusion must be controlled by billing_type, not by deleting reservation_id.
      reservation_id: existingReservationId,

      // ✅ New important field
      billing_type: billingType,
    });

    if (!response?.success) {
      toast.error(response?.message || "Failed to apply billing");
      return;
    }

    toast.success("Billing updated successfully!");
    await fetchCustomers();

    const updated = await getCustomerbillAPI(billingCustomerId);
    if (updated?.success) setBillData(updated);

    handleBillingModalClose();

    const hotelDetails = await fetchHotelDetails();
    const finalBillData = updated?.success ? updated : billData;

    if (billingAction === "PRINT") {
      printBill(
        billingCustomerId,
        finalBillData,
        hotelDetails,
        response.billing_breakdown
      );
    } else {
      downloadBill(
        billingCustomerId,
        finalBillData,
        hotelDetails,
        response.billing_breakdown
      );
    }
  } catch (e) {
    console.error(e);
    toast.error("Error applying billing");
  }
};

  // ── Hotel details helper ────────────────────────────────────────────────
  const fetchHotelDetails = async () => {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    let d = {
      hotelName   : u?.hotel?.hotel_name || u?.hotel_name || "Hotel",
      hotelImage  : u?.hotel?.hotel_image || u?.hotel_image,
      hotelAddress: u?.hotel?.address || "",
      hotelCity   : u?.hotel?.city || "",
      hotelState  : u?.hotel?.state || "",
      hotelGst    : u?.hotel?.gst_number || "",
      hotelMobile : u?.hotel?.owner?.mobile || "",
      hotelEmail  : u?.hotel?.owner?.email || "",
    };
    try {
      const res = await gethotelinfoIdAPI(hotel_id1);
      if (res?.success && res?.data) {
        const h = res.data;
        d = {
          hotelName   : h.hotel_name    || d.hotelName,
          hotelImage  : h.hotel_image   || d.hotelImage,
          hotelAddress: h.address       || d.hotelAddress,
          hotelCity   : h.city          || d.hotelCity,
          hotelState  : h.state         || d.hotelState,
          hotelGst    : h.gst_number    || d.hotelGst,
          hotelMobile : h.hotel_mobile  || h.owner?.mobile || d.hotelMobile,
          hotelEmail  : h.hotel_email   || h.owner?.email  || d.hotelEmail,
        };
      }
    } catch { /* ignore */ }
    return d;
  };

  // ─── Invoice HTML builder (proportional per-dish GST breakdown) ──────────
  const buildBillHtml = (customerId, billDataArg, hotelDetails, billingBreakdown) => {
    const {
      hotelName, hotelImage, hotelAddress, hotelCity, hotelState,
      hotelGst, hotelMobile, hotelEmail,
    } = hotelDetails;
    const { customerName, mobileNumber, menuItems = [] } = billDataArg || {};

    const fmt = (n) =>
      parseFloat(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const addressLine = [hotelAddress, hotelCity, hotelState].filter(Boolean).join(", ");

    // Use server billing breakdown if available, else FE calcResult
    const lines = billingBreakdown?.lines || calcResult?.lines || [];
    const grandTotal    = billingBreakdown?.grand_total   || calcResult?.grandTotal   || 0;
    const totalDiscount = billingBreakdown?.total_discount || calcResult?.discount     || 0;
    const totalGst      = billingBreakdown?.total_gst      || calcResult?.totalGst     || 0;
    const subtotal      = billingBreakdown?.subtotal       || calcResult?.subtotal      || 0;

    // Separate GST and non-GST lines for invoice
    const gstLines    = lines.filter((l) => (l.gst_rate_percent ?? l.gst_rate * 100) > 0);
    const nonGstLines = lines.filter((l) => (l.gst_rate_percent ?? l.gst_rate * 100) === 0);

    const invoiceNumber = menuItems?.[0]?.billingData?.invoice_number || `FOOD-${customerId}-${Date.now()}`;

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${hotelName} - Restaurant Invoice</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A4 portrait; margin: 15mm 12mm; }
    body {
      font-family: 'Helvetica Neue', Arial, sans-serif;
      font-size: 11px; color: #2d2d2d;
      background: #fff; width: 210mm; margin: 0 auto; padding: 10mm 12mm;
    }
    .hotel-header { text-align: center; padding-bottom: 12px; margin-bottom: 6px; border-bottom: 3px solid #1a237e; }
    .hotel-header img { display: block; margin: 0 auto 8px; max-width: 100px; max-height: 65px; object-fit: contain; }
    .hotel-name { font-size: 18px; font-weight: 800; color: #1a237e; text-transform: uppercase; letter-spacing: 2px; }
    .hotel-address { text-align: center; font-size: 9.5px; margin: 5px 0 8px; color: #666; line-height: 1.8; }
    .invoice-header { text-align: center; margin: 10px 0 8px; }
    .invoice-header h2 { font-size: 13px; font-weight: 700; color: #37474f; text-transform: uppercase; letter-spacing: 2px; }
    .inv-num { display: inline-block; font-size: 11px; font-weight: 700; color: #1a237e; background: #e8eaf6; padding: 4px 14px; border-radius: 20px; margin: 5px 0 2px; }
    .inv-date { font-size: 10px; color: #888; margin-top: 2px; }
    .pay-status { display: inline-block; padding: 3px 12px; border-radius: 20px; font-weight: 700; font-size: 10px; margin-top: 5px; text-transform: uppercase; }
    .s-paid    { background: #e8f5e9; color: #1b5e20; border: 1.5px solid #43a047; }
    .s-unpaid  { background: #fff3e0; color: #bf360c; border: 1.5px solid #fb8c00; }
    .s-pending { background: #fff8e1; color: #f57f17; border: 1.5px solid #fdd835; }
    .guest-box { margin: 10px 0 8px; padding: 10px 14px; background: #f5f7ff; border-radius: 6px; border-left: 4px solid #3f51b5; font-size: 10.5px; }
    .guest-box p { margin: 2px 0; line-height: 1.6; }
    .items-table { width: 100%; border-collapse: collapse; margin: 10px 0 0; font-size: 10px; }
    .items-table thead tr { background: #263238; color: #fff; }
    .items-table th { padding: 8px 9px; font-size: 9.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; }
    .items-table td { padding: 7px 9px; border-bottom: 1px solid #eeeeee; }
    .section-gst td { background: #e8f5e9; color: #1b5e20; font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 5px 9px; border-top: 1px solid #c8e6c9; }
    .section-nongst td { background: #fff8e1; color: #e65100; font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 5px 9px; border-top: 1px solid #ffe082; }
    .subtotal-gst td { background: #f1f8e9; font-size: 9.5px; font-weight: 600; font-style: italic; padding: 6px 9px; border-top: 1.5px solid #aed581; border-bottom: 1.5px solid #aed581; }
    .subtotal-nongst td { background: #fff3e0; font-size: 9.5px; font-weight: 600; font-style: italic; padding: 6px 9px; border-top: 1.5px solid #ffcc80; border-bottom: 1.5px solid #ffcc80; }
    .summary-wrapper { margin-top: 12px; display: flex; justify-content: flex-end; }
    .summary-box { width: 280px; border: 1px solid #e0e0e0; border-radius: 6px; overflow: hidden; }
    .summary-box table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    .summary-box td { padding: 6px 12px; border-bottom: 1px solid #f0f0f0; }
    .summary-box td:last-child { text-align: right; }
    .summary-box .grand-total td { background: #1a237e; color: #fff; font-size: 13px; font-weight: 700; padding: 9px 12px; }
    .gst-details { margin-top: 10px; font-size: 9.5px; color: #555; background: #f5f5f5; padding: 9px 12px; border-radius: 5px; border-left: 3px solid #90a4ae; }
    .footer { margin-top: 14px; text-align: center; font-size: 9.5px; color: #9e9e9e; padding-top: 10px; border-top: 1px dashed #e0e0e0; }
    @media print { body { width: 100% !important; padding: 5mm !important; margin: 0 !important; } }
  </style>
</head>
<body>
<div>
  <div class="hotel-header">
    ${hotelImage ? `<img src="${BASE_URL_for_images}/${hotelImage}" alt="${hotelName}" />` : ""}
    <div class="hotel-name">${hotelName}</div>
  </div>
  <div class="hotel-address">
    ${addressLine ? `${addressLine}<br/>` : ""}
    ${hotelGst && hotelGst !== "null" ? `GST No: ${hotelGst}<br/>` : ""}
    ${hotelMobile ? `📞 ${hotelMobile}` : ""}${hotelMobile && hotelEmail ? " | " : ""}${hotelEmail ? `✉ ${hotelEmail}` : ""}
  </div>
  <div class="invoice-header">
    <h2>RESTAURANT INVOICE</h2>
    <div class="inv-num">Invoice No: ${invoiceNumber}</div>
    <div class="inv-date">Date: ${new Date().toLocaleDateString("en-GB")}</div>
    <div>
      <span class="pay-status ${billingStatus === "PAID" ? "s-paid" : billingStatus === "PENDING" ? "s-pending" : "s-unpaid"}">
        ${billingStatus === "PAID" ? "✓ PAID" : billingStatus === "PENDING" ? "⏳ PENDING" : "✗ UNPAID"}
      </span>
    </div>
  </div>
  <div class="guest-box">
    <p><strong>Guest:</strong> ${customerName || "N/A"}</p>
    <p><strong>Mobile:</strong> ${mobileNumber || "N/A"}</p>
    <p><strong>Date:</strong> ${new Date().toLocaleString("en-GB")}</p>
  </div>

  <table class="items-table">
    <thead>
      <tr>
        <th style="text-align:left;width:28%;">Item</th>
        <th style="text-align:left;width:10%;">Type</th>
        <th style="text-align:right;width:10%;">Rate</th>
        <th style="text-align:right;width:8%;">Qty</th>
        <th style="text-align:right;width:12%;">Base Amt</th>
        <th style="text-align:right;width:10%;">Discount</th>
        <th style="text-align:right;width:10%;">Net Amt</th>
        <th style="text-align:center;width:8%;">GST</th>
        <th style="text-align:right;width:10%;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${(() => {
        if (!lines.length) {
          // Fallback: plain menu items
          return (menuItems || []).map((item) => `
            <tr>
              <td>${item.dish}</td><td>${item.type || ""}</td>
              <td style="text-align:right;">₹${fmt(item.cost)}</td>
              <td style="text-align:right;">${item.quantity}</td>
              <td style="text-align:right;">₹${fmt(item.cost * item.quantity)}</td>
              <td style="text-align:right;">—</td>
              <td style="text-align:right;">₹${fmt(item.cost * item.quantity)}</td>
              <td style="text-align:center;color:#999;">N/A</td>
              <td style="text-align:right;font-weight:600;">₹${fmt(item.cost * item.quantity)}</td>
            </tr>`).join("");
        }

        let rows = "";
        if (gstLines.length > 0) {
          rows += `<tr class="section-gst"><td colspan="9">GST APPLICABLE ITEMS</td></tr>`;
          gstLines.forEach((l) => {
            const menuItem = (menuItems || []).find((m) => m.menuId === l.dish_id);
            rows += `<tr>
              <td><strong>${l.dish_name}</strong></td>
              <td>${menuItem?.type || ""}</td>
              <td style="text-align:right;">₹${fmt(l.rate)}</td>
              <td style="text-align:right;">${l.qty}</td>
              <td style="text-align:right;">₹${fmt(l.line_amount ?? l.lineAmt)}</td>
              <td style="text-align:right;color:#e65100;">${(l.line_discount ?? l.lineDiscount) > 0 ? `−₹${fmt(l.line_discount ?? l.lineDiscount)}` : "—"}</td>
              <td style="text-align:right;">₹${fmt(l.net_line_amount ?? l.netAmt)}</td>
              <td style="text-align:center;color:#1a7a3c;font-weight:600;">${l.gst_rate_percent ?? (l.gst_rate * 100)}%<br/><span style="font-size:9px;font-weight:400;">₹${fmt(l.line_gst ?? l.lineGst)}</span></td>
              <td style="text-align:right;font-weight:600;">₹${fmt(l.line_total ?? l.lineTotal)}</td>
            </tr>`;
          });
          const gB = gstLines.reduce((s, l) => s + (l.line_amount ?? l.lineAmt ?? 0), 0);
          const gD = gstLines.reduce((s, l) => s + (l.line_discount ?? l.lineDiscount ?? 0), 0);
          const gN = gstLines.reduce((s, l) => s + (l.net_line_amount ?? l.netAmt ?? 0), 0);
          const gG = gstLines.reduce((s, l) => s + (l.line_gst ?? l.lineGst ?? 0), 0);
          const gT = gstLines.reduce((s, l) => s + (l.line_total ?? l.lineTotal ?? 0), 0);
          rows += `<tr class="subtotal-gst">
            <td colspan="4" style="padding:4px 8px;font-style:italic;">GST Items Subtotal</td>
            <td style="text-align:right;">₹${fmt(gB)}</td>
            <td style="text-align:right;">${gD > 0 ? `−₹${fmt(gD)}` : "—"}</td>
            <td style="text-align:right;">₹${fmt(gN)}</td>
            <td style="text-align:center;">₹${fmt(gG)}</td>
            <td style="text-align:right;font-weight:700;">₹${fmt(gT)}</td>
          </tr>`;
        }

        if (nonGstLines.length > 0) {
          rows += `<tr class="section-nongst"><td colspan="9">NON-GST ITEMS</td></tr>`;
          nonGstLines.forEach((l) => {
            const menuItem = (menuItems || []).find((m) => m.menuId === l.dish_id);
            rows += `<tr>
              <td><strong>${l.dish_name}</strong></td>
              <td>${menuItem?.type || ""}</td>
              <td style="text-align:right;">₹${fmt(l.rate)}</td>
              <td style="text-align:right;">${l.qty}</td>
              <td style="text-align:right;">₹${fmt(l.line_amount ?? l.lineAmt)}</td>
              <td style="text-align:right;color:#e65100;">${(l.line_discount ?? l.lineDiscount) > 0 ? `−₹${fmt(l.line_discount ?? l.lineDiscount)}` : "—"}</td>
              <td style="text-align:right;">₹${fmt(l.net_line_amount ?? l.netAmt)}</td>
              <td style="text-align:center;color:#999;font-size:9px;">Not Applicable</td>
              <td style="text-align:right;font-weight:600;">₹${fmt(l.line_total ?? l.lineTotal)}</td>
            </tr>`;
          });
          const nB = nonGstLines.reduce((s, l) => s + (l.line_amount ?? l.lineAmt ?? 0), 0);
          const nD = nonGstLines.reduce((s, l) => s + (l.line_discount ?? l.lineDiscount ?? 0), 0);
          const nN = nonGstLines.reduce((s, l) => s + (l.net_line_amount ?? l.netAmt ?? 0), 0);
          const nT = nonGstLines.reduce((s, l) => s + (l.line_total ?? l.lineTotal ?? 0), 0);
          rows += `<tr class="subtotal-nongst">
            <td colspan="4" style="padding:4px 8px;font-style:italic;">Non-GST Items Subtotal</td>
            <td style="text-align:right;">₹${fmt(nB)}</td>
            <td style="text-align:right;">${nD > 0 ? `−₹${fmt(nD)}` : "—"}</td>
            <td style="text-align:right;">₹${fmt(nN)}</td>
            <td style="text-align:center;">—</td>
            <td style="text-align:right;font-weight:700;">₹${fmt(nT)}</td>
          </tr>`;
        }
        return rows;
      })()}
    </tbody>
  </table>

  <div class="summary-wrapper">
    <div class="summary-box">
      <table>
        <tr><td style="color:#666;">Base Amount</td><td style="font-weight:600;">₹${fmt(subtotal)}</td></tr>
        <tr><td style="color:#666;">Discount</td><td style="font-weight:600;color:#e65100;">− ₹${fmt(totalDiscount)}</td></tr>
        ${gstLines.length > 0
          ? `<tr><td style="color:#666;">Total GST</td><td style="font-weight:600;color:#1a7a3c;">₹${fmt(totalGst)}</td></tr>`
          : `<tr><td style="color:#999;">GST</td><td style="text-align:right;color:#999;">Not Applicable</td></tr>`}
        <tr class="grand-total"><td>Grand Total</td><td>₹${fmt(grandTotal)}</td></tr>
      </table>
    </div>
  </div>

  ${totalGst > 0 ? `
  <div class="gst-details">
    <p><strong>GST Breakup (CGST + SGST):</strong></p>
    ${gstLines.map((l) => {
      const gstPct = l.gst_rate_percent ?? (l.gst_rate * 100);
      const gstAmt = l.line_gst ?? l.lineGst ?? 0;
      return `<p style="display:flex;justify-content:space-between;">
        <span>${l.dish_name} @ ${gstPct}%</span>
        <span>CGST ${gstPct/2}% = ₹${fmt(gstAmt/2)} | SGST ${gstPct/2}% = ₹${fmt(gstAmt/2)}</span>
      </p>`;
    }).join("")}
    <p style="border-top:1px solid #e0e0e0;margin-top:5px;padding-top:5px;">
      <strong>Total GST: ₹${fmt(totalGst)}</strong>
    </p>
  </div>` : `<div class="gst-details" style="color:#999;"><p>All items are non-GST. No tax applicable.</p></div>`}

  <div class="footer">
    <p>Thank you for dining at ${hotelName}!</p>
    <p><small>This is a computer-generated invoice.</small></p>
  </div>
</div>
</body>
</html>`;
  };

  const downloadBill = async (customerId, billDataArg, hotelDetails, billingBreakdown) => {
    const html  = buildBillHtml(customerId, billDataArg, hotelDetails, billingBreakdown);
    const blob  = new Blob([html], { type: "text/html" });
    const link  = document.createElement("a");
    link.href   = URL.createObjectURL(blob);
    link.download = `${hotelDetails.hotelName}_bill_${customerId}.html`;
    link.click();
  };

  const printBill = (customerId, billDataArg, hotelDetails, billingBreakdown) => {
    const html   = buildBillHtml(customerId, billDataArg, hotelDetails, billingBreakdown);
    const win    = window.open("", "Print-Window");
    if (!win) { toast.error("Popup blocked. Allow popups to print."); return; }
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.print(); win.onafterprint = () => win.close(); };
  };

  // ── Excel export (unchanged logic) ──────────────────────────────────────
  const fetchAllCustomersForExport = async (startDate = "", endDate = "") => {
    try {
      setExportLoading(true);
      const response = await getAllCustomerAPI({
        hotel_id: hotel_id1, currentPage: 1, pageSize: 10000,
        startDate: startDate || undefined, endDate: endDate || undefined,
      });
      return response?.success ? response?.data?.customers || [] : customers;
    } catch { return customers; } finally { setExportLoading(false); }
  };

  const handleExportToExcel = async (startDate = "", endDate = "") => {
    try {
      setExportLoading(true);
      toast.info("Excel export preparing...");
      const allData = await fetchAllCustomersForExport(startDate, endDate);
      if (!allData?.length) { toast.warning("No data to export."); return; }

      const wb  = XLSX.utils.book_new();
      const hn  = user?.hotel?.hotel_name || user?.hotel_name || "Hotel";

      const headerRow = [
        "Sr No","Customer Name","Mobile","Billing Status",
        "Base Subtotal (₹)","Discount (₹)","After Discount (₹)",
        "GST %","GST Amount (₹)","CGST (₹)","SGST (₹)",
        "Total Billed (₹)","Pending (₹)",
      ];

      const dataRows = allData.map((c, i) => {
        const sub  = Number(c?.billingAmounts?.allBillingsSubtotal  ?? 0) || 0;
        const disc = Number(c?.billingAmounts?.allBillingsDiscount  ?? 0) || 0;
        const gst  = Number(c?.billingAmounts?.allBillingsGstAmount ?? 0) || 0;
        const tot  = Number(c?.billingAmounts?.originalTotalAmount  ?? 0) || 0;
        const pend = Number(c?.billingAmounts?.totalFinalAmount     ?? 0) || 0;
        const after = Math.max(sub - disc, 0);
        const gstPct = after > 0 ? Math.round((gst / after) * 10000) / 100 : 0;
        return [
          i + 1, c.name || "N/A", c.mobileNumber || "N/A",
          c?.billing_status || "PENDING",
          sub, disc, after, gstPct, gst,
          Math.round(gst / 2 * 100) / 100, Math.round(gst / 2 * 100) / 100,
          tot, pend,
        ];
      });

      const rows = [
        [`RESTAURANT BILLING — ${hn.toUpperCase()}`],
        [`Generated: ${new Date().toLocaleString("en-IN")}`],
        [],
        headerRow,
        ...dataRows,
        [],
        ["","","","TOTAL →",
          allData.reduce((s,c) => s+(Number(c?.billingAmounts?.allBillingsSubtotal??0)||0),0),
          allData.reduce((s,c) => s+(Number(c?.billingAmounts?.allBillingsDiscount??0)||0),0),
          allData.reduce((s,c) => s+Math.max((Number(c?.billingAmounts?.allBillingsSubtotal??0)||0)-(Number(c?.billingAmounts?.allBillingsDiscount??0)||0),0),0),
          "",
          allData.reduce((s,c) => s+(Number(c?.billingAmounts?.allBillingsGstAmount??0)||0),0),
          "",""       ,
          allData.reduce((s,c) => s+(Number(c?.billingAmounts?.originalTotalAmount??0)||0),0),
          allData.reduce((s,c) => s+(Number(c?.billingAmounts?.totalFinalAmount??0)||0),0),
        ],
      ];

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [6,24,15,16,18,14,24,8,16,12,12,18,18].map((w) => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws, "Restaurant Billing");
      XLSX.writeFile(wb, `${hn.replace(/[^a-z0-9]/gi,"_")}_Restaurant_Billing${startDate?"_"+startDate:""}${endDate?"_to_"+endDate:""}.xlsx`);
      toast.success(`Exported ${allData.length} records`);
    } catch (e) { console.error(e); toast.error("Excel export failed."); }
    finally { setExportLoading(false); }
  };

  // ── Add customer modal (unchanged) ──────────────────────────────────────
  const handleSubmit = async () => {
    const { mobile_number, name } = formData;
    if (!mobile_number) return toast.error("Mobile number is required.");
    if (!name) return toast.error("Name is required.");
    try {
      const matchedGuest = guests.find((g) => g.mobile_number === mobile_number);
      const response = await addcustomerAPI({
        mobileNumber: mobile_number, name,
        guest_id: matchedGuest?.guest_id || null,
      });
      if (response?.success) { toast.success("Customer added"); fetchCustomers(); }
      else toast.error(response?.message || "Failed to add customer");
    } catch { toast.error("Error adding customer"); }
    setShowModal(false); setFormData({ mobile_number: "", name: "" });
  };

  const handleDeleteCustomer = async () => {
    try {
      const response = await deleteCustomerAPI(deleteCustomerId);
      if (response?.success) { toast.success("Deleted."); fetchCustomers(); }
      else toast.error(response?.message || "Failed.");
    } catch { toast.error("Error."); }
    setShowDeleteModal(false); setDeleteCustomerId(null);
  };

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="container px-3 py-5" style={{ marginTop: "5%" }}>
      <ToastContainer />
      <Breadcrumbs title="Portal" breadcrumbItem="Customer Management" />

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="bills">
          <div className="d-flex justify-content-end flex-column flex-md-row gap-2 mb-3">
            <Button
              variant="secondary"
              onClick={() => navigation("/dashboard")}
              className="w-100 w-md-auto"
            >
              Back
            </Button>
            <Button variant="success" onClick={() => {
              const end = new Date(); const start = new Date();
              start.setDate(start.getDate() - 30);
              setExportDateRange({
                startDate: start.toISOString().split("T")[0],
                endDate  : end.toISOString().split("T")[0],
              });
              setShowExportModal(true);
            }} disabled={exportLoading}>
              <FaFileExcel className="me-1" />{exportLoading ? "Exporting..." : "Export to Excel"}
            </Button>
            <Button variant="info" onClick={() => navigation("/hotelmenu")}>Menu Page</Button>
            <Button variant="primary" onClick={() => setShowModal(true)}><FaPlus /> Add Customer</Button>
          </div>

          <div className="table-responsive">
            <Table className="table-bordered">
              <thead>
                <tr className="text-center">
                <th>#</th><th>Guest Name</th>
<th className="d-none d-md-table-cell">Mobile</th>
<th className="d-none d-lg-table-cell">Total Cost</th>
<th className="d-none d-md-table-cell">Payment</th>  {/* ✅ ADD */}
<th>Actions</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {customers.map((customer, index) => {
                  const originalAmount = Number(customer?.billingAmounts?.originalTotalAmount ?? 0) || 0;
                  const pendingAmount  = Number(customer?.billingAmounts?.totalFinalAmount ?? 0) || 0;
                  const status = customer?.billing_status || "PENDING";
                  return (
                    <tr key={customer.id}>
                      <td>{(currentPage - 1) * pageSize + index + 1}</td>
                      <td>
                        <div className="d-flex flex-column">
                          <span>{customer.name}</span>
                          <small className="text-muted d-md-none">{customer.mobileNumber}</small>
                          <small className="text-muted d-md-none">₹{originalAmount.toFixed(2)}</small>
                        </div>
                      </td>
                      <td className="d-none d-md-table-cell">{customer.mobileNumber}</td>
                     <td className="d-none d-lg-table-cell">
  {customer?.hasBillingData ? (
    <span>
      ₹{originalAmount.toFixed(2)}
      {Number(customer?.billingAmounts?.allBillingsDiscount ?? 0) > 0 && (
        <small style={{ display: "block", color: "#e65100", fontSize: "10px" }}>
          disc: ₹{Number(customer.billingAmounts.allBillingsDiscount).toFixed(2)}
        </small>
      )}
      {Number(customer?.billingAmounts?.allBillingsGstAmount ?? 0) > 0 && (
        <small style={{ display: "block", color: "#1a7a3c", fontSize: "10px" }}>
          GST: ₹{Number(customer.billingAmounts.allBillingsGstAmount).toFixed(2)}
        </small>
      )}
      {status === "PARTIALLY_PAID" && (
        <small style={{ display: "block", color: "#f57f17", fontSize: "10px" }}>
          pending: ₹{pendingAmount.toFixed(2)}
        </small>
      )}
    </span>
  ) : (
    // No billing yet — show raw base price in grey (same as SPA)
    <span style={{ color: "#888" }}>
      ₹{originalAmount.toFixed(2)}
      <small style={{ display: "block", fontSize: "10px", color: "#bbb" }}>not billed</small>
    </span>
  )}
</td>
                      
                    
<td className="d-none d-md-table-cell">
  <PaymentStatusBadge status={status} />
</td>

                      <td>
                        <div className="d-flex flex-column flex-md-row gap-1 justify-content-center">
                          <Link to={`/addmenu-details/${customer.id}`}>
                            <Button variant="success" size="sm">View Details</Button>
                          </Link>
                          {(customer?.hasBillingData || pendingAmount > 0) && (
                            <>
                              <Button variant="outline-primary" size="sm"
                                onClick={() => handleBillingModalShow(customer.id, "DOWNLOAD")}>
                                <FaEdit /> Bill
                              </Button>
                              <Button variant="outline-warning" size="sm"
                                onClick={() => handleBillingModalShow(customer.id, "PRINT")}>
                                <FaPrint />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Tab>
      </Tabs>

      <Pagination className="mt-3">
        <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} />
        {[...Array(totalPages).keys()].map((p) => (
          <Pagination.Item key={p} active={p + 1 === currentPage} onClick={() => setCurrentPage(p + 1)}>
            {p + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next onClick={() => setCurrentPage((p) => (p < totalPages ? p + 1 : p))} />
      </Pagination>

      {/* ── Add Customer Modal ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton><Modal.Title>Add Customer</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control type="text" value={formData.mobile_number}
                onChange={async (e) => {
                  const mob = e.target.value;
                  const local = guests.find((g) => g.mobile_number === mob);
                  let matched = local
                    ? { name: `${local.first_name} ${local.last_name}`, guest_id: local.guest_id }
                    : null;
                  setFormData({ mobile_number: mob,
                    name: matched ? matched.name : "",
                    guest_id: matched?.guest_id || null });
                  setIsNameEditable(!matched);
                  // The loaded guest list is only one page; ask the server by number.
                  if (!matched && mob.length >= 10) {
                    const found = await lookupGuestByMobile(mob);
                    if (found) {
                      setFormData((prev) =>
                        prev.mobile_number === mob
                          ? { mobile_number: mob, name: found.name, guest_id: found.guest_id }
                          : prev
                      );
                      setIsNameEditable(false);
                    }
                  }
                }}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={formData.name}
                readOnly={!isNameEditable}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          <Button variant="primary" onClick={handleSubmit}>Add Customer</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton><Modal.Title>Confirm Delete</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to delete this customer?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteCustomer}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          ✅ BILLING WIZARD MODAL — 3 steps (mirrors SPA billing)
      ══════════════════════════════════════════════════════════ */}
      <Modal show={showBillingModal} onHide={handleBillingModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Restaurant Billing &nbsp;
            <small style={{ fontSize: "12px", color: "#888", fontWeight: 400 }}>
              Step {billingStep} of 3
            </small>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {billData && (
            <>
              {/* ── Stepper ── */}
              <div className="d-flex align-items-center mb-4">
                {[{ n: 1, label: "Discount" }, { n: 2, label: "GST Rates" }, { n: 3, label: "Payment" }]
                  .map((s, i) => (
                    <React.Fragment key={s.n}>
                      <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: billingStep >= s.n ? "#1a7a3c" : "#e0e0e0",
                          color: billingStep >= s.n ? "#fff" : "#888",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 14,
                          cursor: billingStep > s.n ? "pointer" : "default",
                        }} onClick={() => { if (billingStep > s.n) setBillingStep(s.n); }}>
                          {billingStep > s.n ? "✓" : s.n}
                        </div>
                        <small style={{
                          marginTop: 4, fontSize: 10,
                          color: billingStep >= s.n ? "#1a7a3c" : "#888",
                          fontWeight: billingStep === s.n ? 700 : 400,
                        }}>{s.label}</small>
                      </div>
                      {i < 2 && (
                        <div style={{
                          flex: 2, height: 2,
                          background: billingStep > s.n ? "#1a7a3c" : "#e0e0e0",
                          marginBottom: 20,
                        }} />
                      )}
                    </React.Fragment>
                  ))}
              </div>

              {/* ── Guest info bar ── */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Guest Name</Form.Label>
                    <Form.Control value={billData.customerName || ""} disabled />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Mobile</Form.Label>
                    <Form.Control value={billData.mobileNumber || ""} disabled />
                  </Form.Group>
                </div>
              </div>

              {/* ══════ STEP 1 — Discount ══════ */}
              {billingStep === 1 && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                    Step 1 — Apply Discount (optional)
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>Total Discount (₹)</Form.Label>
                        <Form.Control type="number" min="0"
                          max={calcResult?.subtotal || 0}
                          value={discountInput}
                         onChange={(e) => {
  let value = parseFloat(e.target.value) || 0;

  const maxAllowed = calcResult?.subtotal || 0;

  if (value > maxAllowed) {
    value = maxAllowed;
    toast.warning(`Discount cannot exceed ₹${maxAllowed.toFixed(2)}`);
  }

  if (value < 0) value = 0;

  setDiscountInput(value);
}}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                          Proportionally split across all dish lines
                        </Form.Text>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>Preview Grand Total (₹)</Form.Label>
                        <Form.Control
                          value={calcResult ? calcResult.grandTotal.toFixed(2) : "—"}
                          readOnly
                          style={{ fontWeight: "bold", background: "#f0fff4", fontSize: 15 }}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                          After discount + GST
                        </Form.Text>
                      </Form.Group>
                    </div>
                  </div>

                  {/* Proportional breakdown table */}
                  {discountInput > 0 && calcResult && (
                    <div style={{ fontSize: 12, background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 4, padding: "8px 12px" }}>
                      <strong>Proportional discount breakdown:</strong>
                      <table style={{ width: "100%", marginTop: 5, fontSize: 11 }}>
                        <thead>
                          <tr style={{ color: "#666" }}>
                            <th style={{ textAlign: "left", paddingBottom: 3 }}>Item</th>
                            <th style={{ textAlign: "right" }}>Base</th>
                            <th style={{ textAlign: "right" }}>Discount Share</th>
                            <th style={{ textAlign: "right" }}>Net</th>
                            <th style={{ textAlign: "right" }}>GST on Net</th>
                            <th style={{ textAlign: "right" }}>Line Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calcResult.lines.map((l) => (
                            <tr key={l.dish_id} style={{ borderTop: "1px solid #ffe082" }}>
                              <td style={{ padding: "3px 0" }}>{l.dish_name}</td>
                              <td style={{ textAlign: "right" }}>₹{l.lineAmt.toFixed(2)}</td>
                              <td style={{ textAlign: "right", color: "#e65100" }}>- ₹{l.lineDiscount.toFixed(2)}</td>
                              <td style={{ textAlign: "right" }}>₹{l.netAmt.toFixed(2)}</td>
                              <td style={{ textAlign: "right", color: "#1a7a3c" }}>
                                {l.lineGst > 0 ? `₹${l.lineGst.toFixed(2)}` : "—"}
                              </td>
                              <td style={{ textAlign: "right", fontWeight: 600 }}>₹{l.lineTotal.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ══════ STEP 2 — GST Rate per dish ══════ */}
              {/* {billingStep === 2 && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                    Step 2 — Set GST Rate per Item
                  </div>
                  <div style={{ fontSize: 11, color: "#3949ab", background: "#f0f4ff", border: "1px solid #c5cae9", borderRadius: 4, padding: "6px 10px", marginBottom: 10 }}>
                    ℹ Enter GST % for each item. Use 0 for non-GST items (e.g. water, bread). GST is calculated on net amount after discount.
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#1e1e1e", color: "#fff" }}>
                        <th style={{ padding: "7px 10px", textAlign: "left" }}>Item</th>
                        <th style={{ padding: "7px 10px", textAlign: "right" }}>Net Price</th>
                        <th style={{ padding: "7px 10px", textAlign: "center", width: 110 }}>GST Rate (%)</th>
                        <th style={{ padding: "7px 10px", textAlign: "right" }}>GST Amt</th>
                        <th style={{ padding: "7px 10px", textAlign: "right" }}>Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let grandNet = 0, grandGst = 0, grandTotal = 0;
                        const rows = (billData.menuItems || []).map((item) => {
                          const line = calcResult?.lines?.find((l) => l.dish_id === item.menuId);
                          const netAmt  = line?.netAmt  ?? (Number(item.cost) * Number(item.quantity));
                          const gstRate = editableGstRates[item.menuId] ?? Number(item.gst_percentage ?? 0);
                          const gstAmt  = parseFloat(((netAmt * gstRate) / 100).toFixed(2));
                          const lineTotal = parseFloat((netAmt + gstAmt).toFixed(2));
                          grandNet += netAmt; grandGst += gstAmt; grandTotal += lineTotal;
                          return (
                            <tr key={item.menuId} style={{ borderBottom: "1px solid #e0e0e0" }}>
                              <td style={{ padding: "8px 10px" }}>{item.dish}</td>
                              <td style={{ padding: "8px 10px", textAlign: "right" }}>
                                ₹{netAmt.toFixed(2)}
                                {line?.lineDiscount > 0 && (
                                  <span style={{ display: "block", fontSize: 9, color: "#e65100" }}>
                                    (after −₹{line.lineDiscount.toFixed(2)})
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                <input type="number" min="0" max="100" step="0.01"
                                  value={editableGstRates[item.menuId] ?? Number(item.gst_percentage ?? 0)}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    const updated = { ...editableGstRates, [item.menuId]: val };
                                    setEditableGstRates(updated);
                                    const lines = buildDishLines(billData.menuItems, updated);
                                    setCalcResult(calcBilling(lines, discountInput));
                                  }}
                                  style={{ width: 75, padding: "4px 6px", border: "1px solid #3f51b5", borderRadius: 4, fontSize: 13, textAlign: "center", background: "#f8f9ff" }}
                                />
                                <span style={{ fontSize: 10, color: "#888", display: "block", marginTop: 2 }}>% GST</span>
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "right" }}>
                                {gstAmt > 0
                                  ? <span style={{ color: "#1a7a3c" }}>₹{gstAmt.toFixed(2)}</span>
                                  : <span style={{ color: "#999" }}>—</span>}
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>
                                ₹{lineTotal.toFixed(2)}
                              </td>
                            </tr>
                          );
                        });
                        return (
                          <>
                            {rows}
                            <tr style={{ background: "#f0fff4", fontWeight: 700, borderTop: "2px solid #1a7a3c" }}>
                              <td style={{ padding: "7px 10px" }}>Total</td>
                              <td style={{ padding: "7px 10px", textAlign: "right" }}>₹{grandNet.toFixed(2)}</td>
                              <td style={{ padding: "7px 10px", textAlign: "center" }}>
                                <span style={{ color: grandGst > 0 ? "#1a7a3c" : "#999" }}>
                                  {grandNet > 0 ? `${((grandGst / grandNet) * 100).toFixed(2)}%*` : "0%"}
                                </span>
                              </td>
                              <td style={{ padding: "7px 10px", textAlign: "right", color: "#1a7a3c" }}>₹{grandGst.toFixed(2)}</td>
                              <td style={{ padding: "7px 10px", textAlign: "right" }}>₹{grandTotal.toFixed(2)}</td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              )} */}

              {/* ══════ STEP 2 — Bill-Level GST ══════ */}
{billingStep === 2 && (
  <div>
    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
      Step 2 — Apply GST (Bill Level)
    </div>

    <div style={{
      fontSize: 11, color: "#1565c0", background: "#e3f2fd",
      border: "1px solid #90caf9", borderRadius: 6,
      padding: "8px 12px", marginBottom: 16,
    }}>
      GST is applied once on the total net bill amount (after discount). Use 0% if no GST applies.
    </div>

    {/* GST rate selector */}
    <div style={{
      background: "#f8f9ff", border: "1px solid #c5cae9",
      borderRadius: 6, padding: 16, marginBottom: 14,
    }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#333", marginBottom: 10 }}>
        GST Rate (%)
      </label>

      {/* Quick-select presets */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {[0, 5, 12, 18, 28].map((pct) => (
          <button
            key={pct}
            type="button"
            onClick={() => {
              // Apply same GST rate to ALL dishes
              const updated = {};
              (billData.menuItems || []).forEach((item) => {
                updated[item.menuId] = pct;
              });
              setEditableGstRates(updated);
              const lines = buildDishLines(billData.menuItems, updated);
              setCalcResult(calcBilling(lines, discountInput));
            }}
            style={{
              padding: "6px 14px", borderRadius: 6, fontSize: 13,
              fontWeight: Object.values(editableGstRates)[0] === pct ? 700 : 500,
              border: Object.values(editableGstRates)[0] === pct
                ? "2px solid #1565c0" : "1.5px solid #b0bec5",
              background: Object.values(editableGstRates)[0] === pct ? "#1565c0" : "#fff",
              color: Object.values(editableGstRates)[0] === pct ? "#fff" : "#455a64",
              cursor: "pointer", minWidth: 52,
            }}
          >
            {pct}%
          </button>
        ))}
        {/* Custom input */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="number" min="0" max="100" step="0.5"
            placeholder="Custom"
            value={Object.values(editableGstRates)[0] ?? 0}
            onChange={(e) => {
              const val = parseFloat(e.target.value) || 0;
              const updated = {};
              (billData.menuItems || []).forEach((item) => {
                updated[item.menuId] = val;
              });
              setEditableGstRates(updated);
              const lines = buildDishLines(billData.menuItems, updated);
              setCalcResult(calcBilling(lines, discountInput));
            }}
            style={{
              width: 80, padding: "6px 8px", fontSize: 13, fontWeight: 600,
              border: "1.5px solid #3f51b5", borderRadius: 6,
              textAlign: "center", color: "#1a237e",
            }}
          />
          <span style={{ fontSize: 13, color: "#555" }}>%</span>
        </div>
      </div>

      {/* Live preview */}
      {calcResult && (
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10,
          background: "#fff", border: "1px solid #e0e0e0",
          borderRadius: 6, padding: 12,
        }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 10, color: "#888", marginBottom: 3 }}>Net Amount</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              ₹{(calcResult.subtotal - calcResult.discount).toFixed(2)}
            </div>
            <div style={{ fontSize: 9, color: "#aaa" }}>after discount</div>
          </div>
          <div style={{ textAlign: "center", borderLeft: "1px solid #eee", borderRight: "1px solid #eee" }}>
            <div style={{ fontSize: 10, color: "#888", marginBottom: 3 }}>
              GST ({Object.values(editableGstRates)[0] ?? 0}%)
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: calcResult.totalGst > 0 ? "#1a7a3c" : "#999" }}>
              {calcResult.totalGst > 0 ? `₹${calcResult.totalGst.toFixed(2)}` : "—"}
            </div>
            <div style={{ fontSize: 9, color: "#aaa" }}>on net amount</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 10, color: "#888", marginBottom: 3 }}>Grand Total</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a237e" }}>
              ₹{calcResult.grandTotal.toFixed(2)}
            </div>
            <div style={{ fontSize: 9, color: "#aaa" }}>incl. GST</div>
          </div>
        </div>
      )}

      {/* CGST / SGST split note */}
      {calcResult?.totalGst > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: "#555", padding: "6px 10px", background: "#f0f4ff", borderRadius: 4 }}>
          CGST ({(Object.values(editableGstRates)[0] ?? 0) / 2}%):
          ₹{(calcResult.totalGst / 2).toFixed(2)}
          &nbsp;+&nbsp;
          SGST ({(Object.values(editableGstRates)[0] ?? 0) / 2}%):
          ₹{(calcResult.totalGst / 2).toFixed(2)}
          &nbsp;= Total GST ₹{calcResult.totalGst.toFixed(2)}
        </div>
      )}
    </div>

    {/* Items included (read-only, for transparency) */}
    <div style={{ fontSize: 11, color: "#666", marginBottom: 4, fontWeight: 600 }}>
      Items included in this bill:
    </div>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead>
        <tr style={{ background: "#37474f", color: "#fff" }}>
          <th style={{ padding: "6px 10px", textAlign: "left" }}>Item</th>
          <th style={{ padding: "6px 10px", textAlign: "right" }}>Base Price</th>
          <th style={{ padding: "6px 10px", textAlign: "right" }}>Discount Share</th>
          <th style={{ padding: "6px 10px", textAlign: "right" }}>Net Amount</th>
        </tr>
      </thead>
      <tbody>
        {(calcResult?.lines || []).map((l) => (
          <tr key={l.dish_id} style={{ borderBottom: "1px solid #e0e0e0" }}>
            <td style={{ padding: "7px 10px" }}>{l.dish_name}</td>
            <td style={{ padding: "7px 10px", textAlign: "right" }}>₹{l.lineAmt.toFixed(2)}</td>
            <td style={{ padding: "7px 10px", textAlign: "right", color: l.lineDiscount > 0 ? "#e65100" : "#999" }}>
              {l.lineDiscount > 0 ? `− ₹${l.lineDiscount.toFixed(2)}` : "—"}
            </td>
            <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>₹{l.netAmt.toFixed(2)}</td>
          </tr>
        ))}
        <tr style={{ background: "#f5f5f5", fontWeight: 700 }}>
          <td style={{ padding: "7px 10px" }}>Total Net</td>
          <td style={{ padding: "7px 10px", textAlign: "right" }}>₹{calcResult?.subtotal?.toFixed(2) ?? "0.00"}</td>
          <td style={{ padding: "7px 10px", textAlign: "right", color: "#e65100" }}>
            {(calcResult?.discount ?? 0) > 0 ? `− ₹${calcResult.discount.toFixed(2)}` : "—"}
          </td>
          <td style={{ padding: "7px 10px", textAlign: "right" }}>
            ₹{((calcResult?.subtotal ?? 0) - (calcResult?.discount ?? 0)).toFixed(2)}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
)}

              {/* ══════ STEP 3 — Payment Status + Summary ══════ */}
              {billingStep === 3 && calcResult && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                    Step 3 — Payment Status &amp; Summary
                  </div>
                  {/* Summary card */}
                  <div style={{ background: "#f0fff4", border: "1px solid #1a7a3c", borderRadius: 6, padding: "12px 16px", marginBottom: 16 }}>
                    <div className="row" style={{ fontSize: 13 }}>
                      <div className="col-6">
                        <div style={{ color: "#555" }}>Base Amount</div>
                        <div style={{ fontWeight: 600 }}>₹{calcResult.subtotal.toFixed(2)}</div>
                      </div>
                      <div className="col-6">
                        <div style={{ color: "#555" }}>Discount</div>
                        <div style={{ fontWeight: 600, color: "#e65100" }}>− ₹{calcResult.discount.toFixed(2)}</div>
                      </div>
                      <div className="col-6 mt-2">
                        <div style={{ color: "#555" }}>Total GST</div>
                        <div style={{ fontWeight: 600, color: "#1a7a3c" }}>₹{calcResult.totalGst.toFixed(2)}</div>
                      </div>
                      <div className="col-6 mt-2">
                        <div style={{ color: "#555" }}>Grand Total</div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: "#1a237e" }}>
                          ₹{calcResult.grandTotal.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>Payment Status</Form.Label>
                        <Form.Select value={billingStatus}
                          onChange={(e) => setBillingStatus(e.target.value)}>
                          <option value="PENDING">⏳ Pending</option>
                          <option value="PAID">✓ Paid</option>
                          {/* <option value="PARTIALLY_PAID">Partially Paid</option> */}
                          <option value="CANCELLED">Cancelled</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>GST Status</Form.Label>
                        <Form.Control
                          value={calcResult.totalGst > 0
                            ? `Applied — ${calcResult.effGstPct}% (weighted avg)`
                            : "Not Applicable"}
                          disabled
                          style={{ color: calcResult.totalGst > 0 ? "#1a7a3c" : "#999", fontWeight: "bold" }}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {billingStep > 1 && (
              <Button variant="outline-secondary" onClick={() => setBillingStep((s) => s - 1)}>
                ← Back
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={handleBillingModalClose}>Cancel</Button>
            {billingStep < 3 ? (
             <Button
  variant="primary"
  onClick={() => {
    if (discountInput > (calcResult?.subtotal || 0)) {
      toast.error("Discount exceeds total amount.");
      return;
    }
    setBillingStep((s) => s + 1);
  }}
>
  Next →
</Button>
            ) : (
              <Button variant="success" onClick={handleApplyBilling}>
                Apply &amp; {billingAction === "PRINT" ? "Print Invoice" : "Download Invoice"}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {/* ── Excel Export Modal ── */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
        <Modal.Header closeButton><Modal.Title>Export Restaurant Billing</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control type="date" value={exportDateRange.startDate}
                  onChange={(e) => setExportDateRange((p) => ({ ...p, startDate: e.target.value }))} />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control type="date" value={exportDateRange.endDate}
                  onChange={(e) => setExportDateRange((p) => ({ ...p, endDate: e.target.value }))} />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>Cancel</Button>
          <Button variant="success"
            disabled={exportLoading || !exportDateRange.startDate || !exportDateRange.endDate}
            onClick={async () => {
              if (new Date(exportDateRange.startDate) > new Date(exportDateRange.endDate)) {
                toast.warning("Start date cannot be after end date.");
                return;
              }
              await handleExportToExcel(exportDateRange.startDate, exportDateRange.endDate);
              setShowExportModal(false);
            }}>
            {exportLoading ? "Exporting..." : "Export to Excel"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerMenu;
